import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LocaleService } from '../services/locale.service';

/**
 * Guard that redirects from URLs without a locale prefix to the locale-prefixed URL
 * (e.g. / or /about → /en or /en/about) using:
 * 1. Stored preference (sessionStorage.locale)
 * 2. Browser language (navigator.language)
 * 3. Default to English
 */
export const redirectToLocalePrefixGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const localeService = inject(LocaleService);

  // Determine the target language
  const targetLang = getPreferredLanguage(localeService);

  // Get the current path without leading slash
  let currentPath = state.url;
  if (currentPath.startsWith('/')) {
    currentPath = currentPath.substring(1);
  }

  // Separate the path segment from query string / fragment so that a root URL
  // like /?utm_source=foo doesn't produce /en/?utm_source=foo (trailing slash
  // causes child path '/' which matches no route and falls to the 404 wildcard).
  const suffixStart = currentPath.search(/[?#]/);
  const pathOnly = suffixStart === -1 ? currentPath : currentPath.substring(0, suffixStart);
  const suffix   = suffixStart === -1 ? '' : currentPath.substring(suffixStart);

  // Build the new URL with language prefix
  const newUrl = pathOnly ? `/${targetLang}/${pathOnly}${suffix}` : `/${targetLang}${suffix}`;

  // Navigate to the language-prefixed URL
  router.navigateByUrl(newUrl);

  return false; // Prevent activation of current route, redirect will handle it
};

/**
 * Determines the preferred language based on:
 * 1. Stored preference in sessionStorage
 * 2. Browser language
 * 3. Default to 'en'
 */
function getPreferredLanguage(localeService: LocaleService): string {
  const supportedCodes = localeService.supportedLocales.map(l => l.code);

  // 1. Check sessionStorage for stored preference
  const storedLang = sessionStorage.getItem('locale');
  if (storedLang && supportedCodes.includes(storedLang)) {
    return storedLang;
  }

  // 2. Check browser language
  const browserLang = navigator.language?.split('-')[0]; // e.g., 'en-US' -> 'en'
  if (browserLang && supportedCodes.includes(browserLang)) {
    return browserLang;
  }

  // 3. Default to English
  return 'en';
}
